import React from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useTranslation } from "react-i18next"
import {
  FileText,
  MapPin,
  Bus,
  Coins,
  Image as ImageIcon,
  X,
  Save,
  PlusCircle,
  Car,
  Loader2,
} from "lucide-react"
import Button from "../../shared/components/ui/Button"
import {
  SectionCard,
  Field,
  TextInput,
  NumberInput,
  SelectInput,
  DateInput,
  Toggle,
  TextArea,
} from "./components/Formcontrols"
import MultiSelectChips from "./components/MultiSelectChips/MultiSelectChips"
import PhotoUploader from "./components/PhotoUploader"
import { usePriceForm } from "./hooks/usePriceForm"

const Grid = ({ cols = "md:grid-cols-2", children }) => (
  <div className={`grid grid-cols-1 gap-4 ${cols}`}>{children}</div>
)

const SelectAllButton = ({ onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-main hover:opacity-80 transition-opacity"
  >
    <PlusCircle className="w-4 h-4" />
    {label}
  </button>
)

const App = ({ priceId = null, urls, prefill = {} }) => {
  const { t } = useTranslation()
  const tt = (key, def) => t(key, { defaultValue: def })

  const {
    form,
    setField,
    options,
    settings,
    resorts,
    isLoadingResorts,
    isLoading,
    isError,
    isSaving,
    isSinglePrice,
    errors,
    serverErrors,
    changeState,
    selectAllResorts,
    selectAllHotels,
    existingPhotos,
    newFiles,
    addFiles,
    removeNewFile,
    removeExistingPhoto,
    deletingPhotoId,
    submit,
  } = usePriceForm({ urls, priceId, prefill })

  if (isLoading || !form) {
    return (
      <div className="flex items-center justify-center py-32 text-text-muted">
        {isError ? (
          tt("admin.load_error", "Не удалось загрузить форму")
        ) : (
          <Loader2 className="w-8 h-8 animate-spin" />
        )}
      </div>
    )
  }

  const isEdit = Boolean(priceId)
  const goToList = () =>
    window.Turbolinks?.visit
      ? window.Turbolinks.visit(urls.prices)
      : window.location.assign(urls.prices)
  const showTransferPrices = settings.transfer_price_source === "for_transfer"
  const invalid = (k) => Boolean(errors[k])

  const priceRows = isSinglePrice
    ? []
    : [
        ["child_sum", "child_sum_netto", t("admin.price_per_child"), t("admin.net_price_kid")],
        [
          "infant_sum",
          "infant_sum_netto",
          t("admin.price_per_infant"),
          t("admin.net_price_infant"),
        ],
      ]

  return (
    <div className="bg-bg-secondary min-h-screen p-3 sm:p-4 md:p-6">
      <div className="max-w-[1400px] mx-auto flex flex-col gap-4 sm:gap-5">
        {/* Шапка */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-app-base-strong">
              {isEdit
                ? tt("admin.edit_route", "Редактирование трассы")
                : tt("admin.create_route", "Создание трассы")}
            </h1>
            <p className="text-text-muted mt-1">
              {tt("admin.route_form_hint", "Заполните параметры трассы, цены и условия трансфера")}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              color="third"
              size="lg"
              rounded="md"
              onClick={goToList}
              disabled={isSaving}
              className="border border-app-content-faint/20"
            >
              <X className="w-4 h-4" />
              {t("admin.cancel")}
            </Button>
            <Button color="second" size="lg" rounded="md" onClick={submit} isLoading={isSaving}>
              {!isSaving && <Save className="w-4 h-4" />}
              {t("admin.save_route", "Сохранить трассу")}
            </Button>
          </div>
        </div>

        {/* Ошибки сервера */}
        <AnimatePresence>
          {serverErrors.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="rounded-card border border-red-200 bg-red-50 text-red-600 p-4 text-sm">
                <ul className="list-disc pl-5">
                  {serverErrors.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Основная информация */}
        <SectionCard index={0} icon={FileText} title={tt("admin.main_info", "Основная информация")}>
          <Grid>
            <Field
              label={t("admin.naming")}
              error={invalid("name") && tt("admin.required", "Обязательное поле")}
            >
              <TextInput
                value={form.name ?? ""}
                invalid={invalid("name")}
                onChange={(e) => setField("name", e.target.value)}
              />
            </Field>
            <Field label={t("admin.country")}>
              <SelectInput
                options={options.states}
                value={form.state_id}
                invalid={invalid("state_id")}
                onChange={changeState}
              />
            </Field>
            <Field label={t("admin.company")}>
              <SelectInput
                options={options.companies}
                value={form.company_id}
                invalid={invalid("company_id")}
                onChange={(v) => setField("company_id", v)}
              />
            </Field>
            <Field label={t("admin.terminal")}>
              <SelectInput
                options={options.terminals}
                value={form.terminal_id}
                invalid={invalid("terminal_id")}
                onChange={(v) => setField("terminal_id", v)}
              />
            </Field>
          </Grid>
        </SectionCard>

        {/* Курорты и отели */}
        <SectionCard
          index={1}
          icon={MapPin}
          title={tt("admin.resorts_and_hotels", "Курорты и отели")}
        >
          <Grid>
            <Field
              label={t("admin.resorts")}
              action={
                resorts.length > 0 && (
                  <SelectAllButton onClick={selectAllResorts} label={t("admin.select_all")} />
                )
              }
            >
              <MultiSelectChips
                options={resorts}
                value={form.region_ids}
                onChange={(v) => setField("region_ids", v)}
                disabled={!form.state_id || isLoadingResorts}
                placeholder={tt("admin.add_resort", "Добавить курорт...")}
                emptyText={
                  form.state_id
                    ? tt("admin.nothing_found", "Ничего не найдено")
                    : tt("admin.choose_country_first", "Сначала выберите страну")
                }
              />
            </Field>
            <Field
              label={`${t("admin.partner_price_provider")} ${t("admin.hotels")}`}
              action={<SelectAllButton onClick={selectAllHotels} label={t("admin.select_all")} />}
            >
              <MultiSelectChips
                options={options.hotel_companies}
                value={form.company_ids}
                onChange={(v) => setField("company_ids", v)}
                placeholder={tt("admin.add_hotel", "Добавить отель...")}
                emptyText={tt("admin.nothing_found", "Ничего не найдено")}
              />
            </Field>
          </Grid>
        </SectionCard>

        {/* Транспорт и период */}
        <SectionCard
          index={2}
          icon={Bus}
          title={tt("admin.transport_and_period", "Транспорт и период")}
        >
          <Grid cols="md:grid-cols-2 xl:grid-cols-4">
            <Field label={t("admin.transport_type")}>
              <SelectInput
                icon={Car}
                options={options.transport_types}
                value={form.transport_type_id}
                invalid={invalid("transport_type_id")}
                onChange={(v) => setField("transport_type_id", v)}
              />
            </Field>
            <Field label={t("admin.date_from_s")}>
              <DateInput
                value={form.from_date}
                invalid={invalid("from_date")}
                onChange={(v) => setField("from_date", v)}
              />
            </Field>
            <Field
              label={t("admin.date_to_po")}
              error={invalid("to_date") && tt("admin.date_range_error", "Проверьте период")}
            >
              <DateInput
                value={form.to_date}
                invalid={invalid("to_date")}
                onChange={(v) => setField("to_date", v)}
              />
            </Field>
            <Field label={t("admin.transfer_type")}>
              <SelectInput
                options={options.transfer_types}
                valueKey="value"
                labelKey="label"
                value={form.transfer_type}
                onChange={(v) => setField("transfer_type", v)}
              />
            </Field>
          </Grid>
        </SectionCard>

        {/* Стоимость */}
        <SectionCard index={3} icon={Coins} title={tt("admin.cost", "Стоимость")}>
          <div className="flex flex-col gap-4">
            <Grid cols="md:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1fr_1fr]">
              <Field label={t("admin.currency")}>
                <SelectInput
                  options={options.valutes}
                  value={form.valute_id}
                  invalid={invalid("valute_id")}
                  onChange={(v) => setField("valute_id", v)}
                />
              </Field>
              <Field label={isSinglePrice ? t("admin.rental_cost") : t("admin.price_per_adult")}>
                <NumberInput value={form.adult_sum} onChange={(v) => setField("adult_sum", v)} />
              </Field>
              <Field
                label={isSinglePrice ? t("admin.rental_cost_nett") : t("admin.net_price_adult")}
              >
                <NumberInput
                  value={form.adult_sum_netto}
                  onChange={(v) => setField("adult_sum_netto", v)}
                />
              </Field>
              <Field label={t("admin.number_of_passengers_from")}>
                <NumberInput
                  value={form.passenger_from}
                  onChange={(v) => setField("passenger_from", v)}
                />
              </Field>
              <Field label={t("admin.number_of_passengers_up_to")}>
                <NumberInput
                  value={form.passenger_to}
                  invalid={invalid("passenger_to")}
                  onChange={(v) => setField("passenger_to", v)}
                />
              </Field>
            </Grid>

            {/* Дети / младенцы — скрываются для rent/private, как в старой форме */}
            <AnimatePresence initial={false}>
              {!isSinglePrice && (
                <motion.div
                  key="extra-prices"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="flex flex-col gap-4 pt-1">
                    <Grid cols="md:grid-cols-2 xl:grid-cols-4">
                      {priceRows.map(([sum, netto, label, labelNetto]) => (
                        <React.Fragment key={sum}>
                          <Field label={label}>
                            <NumberInput value={form[sum]} onChange={(v) => setField(sum, v)} />
                          </Field>
                          <Field label={labelNetto}>
                            <NumberInput value={form[netto]} onChange={(v) => setField(netto, v)} />
                          </Field>
                        </React.Fragment>
                      ))}
                    </Grid>
                    <Grid cols="md:grid-cols-2 xl:grid-cols-4">
                      <Field label={t("admin.max_childs_age")}>
                        <NumberInput
                          value={form.child_age_max}
                          onChange={(v) => setField("child_age_max", v)}
                        />
                      </Field>
                      <Field label={t("admin.max_childs_infant")}>
                        <NumberInput
                          value={form.infant_age_max}
                          onChange={(v) => setField("infant_age_max", v)}
                        />
                      </Field>
                    </Grid>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Цены для transfer-режима */}
            {showTransferPrices && (
              <div className="rounded-card border border-app-content-faint/20 p-4 flex flex-col gap-4">
                <p className="font-semibold text-app-base-strong">{t("admin.transfer_prices")}</p>
                <Grid cols="md:grid-cols-2 xl:grid-cols-4">
                  <Field
                    label={`${isSinglePrice ? t("admin.rental_cost") : t("admin.price_per_adult")} (transfer)`}
                  >
                    <NumberInput
                      value={form.adult_sum_transfer}
                      onChange={(v) => setField("adult_sum_transfer", v)}
                    />
                  </Field>
                  <Field
                    label={`${isSinglePrice ? t("admin.rental_cost_nett") : t("admin.net_price_adult")} (transfer)`}
                  >
                    <NumberInput
                      value={form.adult_sum_transfer_netto}
                      onChange={(v) => setField("adult_sum_transfer_netto", v)}
                    />
                  </Field>
                  {!isSinglePrice && (
                    <>
                      <Field label={`${t("admin.price_per_child")} (transfer)`}>
                        <NumberInput
                          value={form.child_sum_transfer}
                          onChange={(v) => setField("child_sum_transfer", v)}
                        />
                      </Field>
                      <Field label={`${t("admin.net_price_kid")} (transfer)`}>
                        <NumberInput
                          value={form.child_sum_transfer_netto}
                          onChange={(v) => setField("child_sum_transfer_netto", v)}
                        />
                      </Field>
                      <Field label={`${t("admin.price_per_infant")} (transfer)`}>
                        <NumberInput
                          value={form.infant_sum_transfer}
                          onChange={(v) => setField("infant_sum_transfer", v)}
                        />
                      </Field>
                      <Field label={`${t("admin.net_price_infant")} (transfer)`}>
                        <NumberInput
                          value={form.infant_sum_transfer_netto}
                          onChange={(v) => setField("infant_sum_transfer_netto", v)}
                        />
                      </Field>
                    </>
                  )}
                </Grid>
              </div>
            )}
          </div>
        </SectionCard>

        {/* Условия и медиа */}
        <SectionCard
          index={4}
          icon={ImageIcon}
          title={tt("admin.terms_and_media", "Условия и медиа")}
        >
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(220px,1fr)_2fr_minmax(260px,1fr)] gap-6">
            <div className="pt-6">
              <Toggle
                checked={Boolean(form.allow_export_api)}
                onChange={(v) => setField("allow_export_api", v)}
                label={t("admin.allow_export_via_api")}
                hint={tt("admin.allow_export_hint", "Трасса будет доступна во внешнем API")}
              />
            </div>

            <Field label={t("admin.transport_photo")}>
              <PhotoUploader
                existing={existingPhotos}
                files={newFiles}
                onAddFiles={addFiles}
                onRemoveFile={removeNewFile}
                onDeleteExisting={removeExistingPhoto}
                deletingId={deletingPhotoId}
              />
            </Field>

            <div className="grid grid-cols-2 gap-4 content-start pt-0 lg:pt-1">
              <Field label={t("admin.non_refundable_days")}>
                <NumberInput
                  value={form.non_refundable_days}
                  onChange={(v) => setField("non_refundable_days", v)}
                />
              </Field>
              <Field label={t("admin.on_request_days")}>
                <NumberInput
                  value={form.on_request_days}
                  onChange={(v) => setField("on_request_days", v)}
                />
              </Field>
            </div>
          </div>

          <Field
            label={tt("admin.important_info", "Важная информация")}
            hint={tt("admin.important_info_hint", "Показывается в списке цен рядом с трассой")}
            className="mt-4"
          >
            <TextArea
              value={form.important_info ?? ""}
              onChange={(e) => setField("important_info", e.target.value)}
            />
          </Field>
        </SectionCard>
      </div>
    </div>
  )
}

export default App
